import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import OrderTimeline from '../components/OrderTimeline';
import { TrackOrderSEO } from '../components/SEO';
import { toast } from 'sonner';
import { ExternalLink, Mail, Download, FileText, ArrowLeft, Globe, Calendar, Package, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrackOrder = () => {
  const { orderId } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to view order');
      return;
    }

    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrderData(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 text-center">
          <FileText className="w-16 h-16 mx-auto text-[#e5e5e5] mb-4" />
          <h2 className="font-playfair text-2xl font-bold mb-2 text-[#1a1a1a]">Order Not Found</h2>
          <p className="font-manrope text-[#737373] mb-6">The order you're looking for doesn't exist or you don't have access.</p>
          <Link 
            to="/my-orders"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F] transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to My Orders
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { order, publication, package: pkg, status_updates } = orderData;

  const handleDownloadInvoice = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(`${API}/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { pdf, filename } = response.data;
      const blob = new Blob([Uint8Array.from(atob(pdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'paid': 'bg-[#dcfce7] text-[#166534]',
      'content_received': 'bg-[#dbeafe] text-[#1e40af]',
      'under_review': 'bg-[#fef3c7] text-[#92400e]',
      'submitted_to_publisher': 'bg-[#e0e7ff] text-[#3730a3]',
      'published': 'bg-[#D4AF37] text-[#0d0d0d]',
      'live': 'bg-[#D4AF37] text-[#0d0d0d]',
      'completed': 'bg-[#1a1a1a] text-white',
      'payment_failed': 'bg-[#fee2e2] text-[#991b1b]',
      'refunded': 'bg-[#fef3c7] text-[#92400e]'
    };
    return colors[status] || 'bg-[#f5f5f5] text-[#737373]';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'paid': 'Payment Confirmed',
      'content_received': 'Content Received',
      'under_review': 'Under Review',
      'submitted_to_publisher': 'With Publisher',
      'published': 'Published',
      'live': 'Published',
      'completed': 'Completed',
      'payment_failed': 'Payment Failed',
      'refunded': 'Refunded'
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-white">
      <TrackOrderSEO orderId={orderId} />
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link 
            to="/my-orders" 
            className="inline-flex items-center gap-2 font-manrope text-sm text-[#737373] hover:text-[#1a1a1a] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to My Orders
          </Link>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold mb-2 text-[#1a1a1a]">
              Track Order
            </h1>
            <p className="font-manrope text-sm text-[#737373]">
              Order ID: <span className="font-mono text-[#1a1a1a]">{orderId.substring(0, 8).toUpperCase()}</span>
            </p>
          </div>
          <span className={`inline-flex items-center gap-2 px-4 py-2 font-manrope text-xs uppercase tracking-widest ${getStatusColor(order.order_status)}`}>
            {getStatusLabel(order.order_status)}
          </span>
        </div>

        {/* Live Article Banner */}
        {order.live_url && (
          <div className="mb-8 border-2 border-[#D4AF37] bg-[#fef2f2] p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#D4AF37] text-[#0d0d0d] flex items-center justify-center">
                  <Globe className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-playfair text-xl font-bold text-[#1a1a1a]">Your Article is Live!</h3>
                  <p className="font-manrope text-sm text-[#737373]">Click to view your published article</p>
                </div>
              </div>
              <a 
                href={order.live_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="view-live-article-button"
                className="btn-hover inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-xs uppercase tracking-widest hover:bg-[#B8960F]"
              >
                View Live Article
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Timeline */}
          <div className="lg:col-span-2">
            <div className="border border-[#e5e5e5] p-6 md:p-8">
              <h2 className="font-playfair text-2xl font-bold mb-6 text-[#1a1a1a]">Order Progress</h2>
              <OrderTimeline 
                currentStatus={order.order_status}
                statusUpdates={status_updates}
                liveUrl={order.live_url}
              />
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Order Details Card */}
            <div className="border border-[#e5e5e5] p-6">
              <h3 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Order Details</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                  <div>
                    <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Publication</span>
                    <span className="font-manrope text-sm font-semibold text-[#1a1a1a]">{publication.name}</span>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                  <div>
                    <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Package</span>
                    <span className="font-manrope text-sm font-semibold text-[#1a1a1a]">{pkg.name}</span>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                  <div>
                    <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Amount Paid</span>
                    <span className="font-mono text-lg font-bold text-[#1a1a1a]">₹{order.total_amount.toLocaleString('en-IN')}</span>
                  </div>
                </div>
                
                {order.deadline_date && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Expected Delivery</span>
                      <span className="font-manrope text-sm text-[#1a1a1a]">
                        {new Date(order.deadline_date).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                )}
                
                {order.add_ons?.writing_service && (
                  <div className="pt-3 mt-3 border-t border-[#e5e5e5]">
                    <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Add-ons</span>
                    <span className="inline-block px-3 py-1 bg-[#f5f5f5] font-manrope text-xs text-[#525252] capitalize">
                      {order.add_ons.writing_service} Writing
                    </span>
                  </div>
                )}
                
                {order.invoice_id && (
                  <div className="pt-3 mt-3 border-t border-[#e5e5e5]">
                    <span className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest block mb-1">Invoice ID</span>
                    <span className="font-mono text-sm text-[#1a1a1a]">{order.invoice_id}</span>
                  </div>
                )}
              </div>
              
              {order.payment_status === 'paid' && (
                <div className="mt-6 pt-6 border-t border-[#e5e5e5]">
                  <button
                    onClick={handleDownloadInvoice}
                    data-testid="download-invoice-button"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-[#1a1a1a] text-[#1a1a1a] font-manrope text-xs uppercase tracking-widest hover:bg-[#1a1a1a] hover:text-white transition-all duration-300"
                  >
                    <Download className="w-4 h-4" />
                    Download GST Invoice
                  </button>
                </div>
              )}
            </div>
            
            {/* Support Card */}
            <div className="border border-[#e5e5e5] p-6 bg-[#fafafa]">
              <h3 className="font-playfair text-xl font-bold mb-2 text-[#1a1a1a]">Need Help?</h3>
              <p className="font-manrope text-sm text-[#737373] mb-4">
                Have questions about your order? Our support team is here to help.
              </p>
              <a 
                href="mailto:support@businessmintpr.com"
                data-testid="contact-support-button"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a1a1a] text-white font-manrope text-xs uppercase tracking-widest hover:bg-[#333333] transition-all duration-300"
              >
                <Mail className="w-4 h-4" />
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TrackOrder;
